import { Injectable } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Auth,
  AuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  User,
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { environment } from '../../../environments/environment';

export type FederatedProviderId = 'google.com' | 'microsoft.com' | 'apple.com';

/** Perfil mínimo que consume la interfaz; conserva los nombres de campo del perfil Auth0 anterior. */
export interface IdentityProfile {
  name: string;
  given_name: string;
  family_name: string;
  email: string;
  picture?: string;
  sign_in_provider?: string;
}

const IDENTITY_APP_NAME = 'diazar-cloud-identity';

/**
 * Inicio de sesión de Diazar Cloud (Firebase Authentication) para dcw-residentes.
 * Usa una app Firebase con nombre propio para no mezclarse con la app que
 * dcw ya utiliza para Firestore.
 */
@Injectable({ providedIn: 'root' })
export class IdentityService {
  private authPromise?: Promise<Auth>;
  private readyPromise?: Promise<User | null>;

  get isConfigured(): boolean {
    return !!environment.identityFirebase;
  }

  enabledProviders(): Array<{ id: FederatedProviderId; label: string }> {
    const flags = environment.federatedLogin;
    return [
      { id: 'google.com' as const, label: 'Google' },
      ...(flags?.apple ? [{ id: 'apple.com' as const, label: 'Apple' }] : []),
      ...(flags?.microsoft ? [{ id: 'microsoft.com' as const, label: 'Microsoft' }] : []),
    ];
  }

  /** Resuelve el usuario restaurado por Firebase al cargar la aplicación. */
  ready(): Promise<User | null> {
    this.readyPromise ??= this.auth().then(
      (auth) =>
        new Promise<User | null>((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        }),
    ).catch(() => null);
    return this.readyPromise;
  }

  async currentUser(): Promise<User | null> {
    await this.ready();
    return (await this.auth()).currentUser;
  }

  async signInWithEmail(email: string, password: string): Promise<User> {
    const auth = await this.auth();
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  }

  async signInWithProvider(id: FederatedProviderId): Promise<User> {
    const auth = await this.auth();
    const credential = await signInWithPopup(auth, this.createProvider(id));
    return credential.user;
  }

  async sendPasswordReset(email: string): Promise<void> {
    const auth = await this.auth();
    auth.languageCode = 'es';
    await sendPasswordResetEmail(auth, email);
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    const user = await this.currentUser();
    return user ? user.getIdToken(forceRefresh) : null;
  }

  async signOut(): Promise<void> {
    if (!this.isConfigured) return;
    await signOut(await this.auth());
  }

  profileOf(user: User): IdentityProfile {
    const displayName = (user.displayName ?? '').trim();
    const [givenName, ...rest] = displayName.split(/\s+/).filter(Boolean);
    const email = user.email ?? '';
    return {
      name: displayName || email,
      given_name: givenName ?? email.split('@')[0] ?? '',
      family_name: rest.join(' '),
      email,
      picture: user.photoURL ?? undefined,
      sign_in_provider: user.providerData[0]?.providerId ?? 'password',
    };
  }

  private auth(): Promise<Auth> {
    if (!environment.identityFirebase) {
      return Promise.reject(Object.assign(new Error('Identity not configured'), { code: 'auth/operation-not-allowed' }));
    }
    this.authPromise ??= (async () => {
      const app = this.app();
      const auth = getAuth(app);
      await setPersistence(auth, browserLocalPersistence);
      return auth;
    })();
    return this.authPromise;
  }

  private app(): FirebaseApp {
    return getApps().some((app) => app.name === IDENTITY_APP_NAME)
      ? getApp(IDENTITY_APP_NAME)
      : initializeApp(environment.identityFirebase!, IDENTITY_APP_NAME);
  }

  private createProvider(id: FederatedProviderId): AuthProvider {
    if (id === 'google.com') {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      return provider;
    }
    if (!this.enabledProviders().some((provider) => provider.id === id)) {
      throw Object.assign(new Error('Sign-in provider disabled'), { code: 'auth/operation-not-allowed' });
    }
    const provider = new OAuthProvider(id);
    if (id === 'microsoft.com') {
      provider.setCustomParameters({
        prompt: 'select_account',
        tenant: environment.federatedLogin?.microsoftTenant ?? 'common',
      });
    } else {
      provider.addScope('email');
    }
    return provider;
  }
}

/** Mensajes en español para los códigos de Firebase Authentication (mismos textos que Diazar Cloud). */
export function identityErrorMessage(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : '';
  if (code.startsWith('auth/requests-from-referer')) {
    return 'La configuración de acceso no está disponible para este ambiente.';
  }
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/user-disabled':
      return 'El correo o la contraseña no son correctos.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'El acceso fue cancelado. Puedes intentarlo nuevamente.';
    case 'auth/popup-blocked':
      return 'El navegador bloqueó la ventana de acceso. Permite ventanas emergentes e intenta nuevamente.';
    case 'auth/account-exists-with-different-credential':
      return 'Esta cuenta ya utiliza otro método de acceso. Ingresa con el método registrado.';
    case 'auth/too-many-requests':
      return 'Se realizaron demasiados intentos. Espera un momento y vuelve a intentar.';
    case 'auth/network-request-failed':
      return 'No fue posible comunicarse con el servicio de acceso. Verifica tu conexión.';
    case 'auth/operation-not-allowed':
    case 'auth/operation-not-supported-in-this-environment':
    case 'auth/unauthorized-domain':
      return 'La configuración de acceso no está disponible para este ambiente.';
    default:
      return 'No fue posible iniciar la sesión. Intenta nuevamente.';
  }
}
